import { useState } from "react";
import ApiService from "../../services/api.service";
import { normalizeTag, teamTagErrorCopy } from "../../utils/inviteErrorCopy";
import "../client/ClientLayout.css";
import "./CoachPages.css";

// A coach creates their OWN team (POST /coach/teams). Mirrors iOS's
// CoachCreateTeamSheet.
//
// WHY WEB NEEDS THIS AT ALL. The SMS invite link is a WEB url
// (app.polyp.me/join/{token}), so a coach invited to "bring their own team"
// claims it in a browser and lands on /coach. Without this form they'd arrive
// with no team and no way to make one — the flow would simply dead-end on the
// surface it was delivered to.
//
// `tag` is REQUIRED here, unlike the league's own POST /league/teams where it
// is optional: the hashtag is how a league finds and invites the team, so a
// coach-owned team without one is unreachable.
//
// A 201 carrying joinedLeagueId means the server also auto-joined the league
// that invited this coach (Users.invitedByLeagueId -> an ACTIVE affiliation).
// That write is best-effort and silent server-side, so echoing it back is the
// only confirmation the coach ever gets that their team reached the league.
export default function CreateTeamModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [joinedLeagueId, setJoinedLeagueId] = useState(null);

  const canSave = name.trim() && normalizeTag(tag);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await ApiService.createCoachTeam({
        name: name.trim(),
        tag: normalizeTag(tag),
      });
      if (resp?.joinedLeagueId) {
        setJoinedLeagueId(resp.joinedLeagueId);
        setSaving(false);
        return;
      }
      onCreated();
    } catch (err) {
      setError(teamTagErrorCopy(err));
      setSaving(false);
    }
  };

  if (joinedLeagueId) {
    return (
      <div className="coach-modal-backdrop" onClick={onCreated}>
        <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="client-h2">Your team joined the league</h2>
          <p className="client-subtitle">
            Their games will show up in your schedule. The team is yours — you run the roster.
          </p>
          <div className="coach-modal-actions">
            <button className="client-button" onClick={onCreated}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Create Your Team</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="Team name" value={name}
            onChange={(e) => setName(e.target.value)} />
          <input className="client-input" placeholder="#hashtag" value={tag}
            autoCapitalize="none" autoCorrect="off"
            onChange={(e) => setTag(e.target.value)} />
        </div>
        <p className="client-subtitle">
          Your team's public identity on polyp.me — lowercase letters, numbers and _.
          Leagues use it to add your team to their schedule.
        </p>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Creating…" : "Create Team"}
          </button>
        </div>
      </div>
    </div>
  );
}
